import React, { useState } from 'react';
import { ChevronRight, ChevronDown, Calendar, Clock } from 'lucide-react';

const VimshottariDashaComplete = ({ dashaData }) => {
  const [expandedMD, setExpandedMD] = useState(null);
  const [expandedAD, setExpandedAD] = useState({});
  const [expandedPD, setExpandedPD] = useState({});

  if (!dashaData) {
    return (
      <div className="text-white/50 text-center py-8">
        No Vimshottari Dasha data available
      </div>
    );
  }

  // Ensure we have arrays (handle case where backend returns {} on error)
  const ensureArray = (data) => Array.isArray(data) ? data : [];
  
  const mahadashas = ensureArray(dashaData['11.1_vimshottari_md']);
  const antardashas = ensureArray(dashaData['11.1_vimshottari_ad']);
  const pratyantardashas = ensureArray(dashaData['11.1_vimshottari_pd']);
  const sookshmadashas = ensureArray(dashaData['11.1_vimshottari_sd']);
  
  // Check if we have any data
  if (mahadashas.length === 0 && antardashas.length === 0) {
    return (
      <div className="text-white/50 text-center py-8">
        No Vimshottari Dasha data available
      </div>
    );
  }

  // Planet colors
  const planetColors = {
    'Sun': 'bg-yellow-500',
    'Moon': 'bg-blue-300',
    'Mars': 'bg-red-500',
    'Mercury': 'bg-green-400',
    'Jupiter': 'bg-yellow-400',
    'Venus': 'bg-pink-400',
    'Saturn': 'bg-purple-600',
    'Rahu': 'bg-gray-700',
    'Ketu': 'bg-gray-500'
  };

  const getPlanetColor = (planet) => planetColors[planet] || 'bg-gray-400';

  // Helper to format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  // Calculate duration in years
  const calculateDuration = (startStr, endStr) => {
    try {
      const start = new Date(startStr);
      const end = new Date(endStr);
      const diffMs = end - start;
      const years = diffMs / (1000 * 60 * 60 * 24 * 365.25);
      return years.toFixed(2);
    } catch {
      return '-';
    }
  };

  // Check if period is current
  const isCurrent = (startStr, endStr) => {
    try {
      const now = new Date();
      const start = new Date(startStr);
      const end = new Date(endStr);
      return now >= start && now <= end;
    } catch {
      return false;
    }
  };

  // Toggle functions
  const toggleMD = (index) => {
    setExpandedMD(expandedMD === index ? null : index);
  };

  const toggleAD = (mdIndex, adIndex) => {
    const key = `${mdIndex}-${adIndex}`;
    setExpandedAD(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const togglePD = (mdIndex, adIndex, pdIndex) => {
    const key = `${mdIndex}-${adIndex}-${pdIndex}`;
    setExpandedPD(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Get Antardashas for a Mahadasha
  const getAntardashas = (mdPlanet) => {
    return antardashas.filter(ad => ad.MD === mdPlanet);
  };

  // Get Pratyantardashas for an Antardasha
  const getPratyantardashas = (mdPlanet, adPlanet) => {
    return pratyantardashas.filter(pd => pd.MD === mdPlanet && pd.AD === adPlanet);
  };

  // Get Sookshma Dashas for a Pratyantardasha
  const getSookshmadashas = (mdPlanet, adPlanet, pdPlanet) => {
    return sookshmadashas.filter(sd => 
      sd.MD === mdPlanet && sd.AD === adPlanet && sd.PD === pdPlanet
    );
  };

  return (
    <div className="w-full space-y-4">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-900/50 to-purple-900/50 rounded-lg p-6 border border-indigo-500/30">
        <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-purple-400 mb-2">
          Vimshottari Dasha System (120-Year Cycle)
        </h2>
        <p className="text-white/70 text-sm">
          Complete hierarchical view: Mahadasha → Antardasha → Pratyantardasha → Sookshma Dasha
        </p>
        <div className="mt-4 flex gap-4 text-xs">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-cyan-400" />
            <span className="text-white/60">{mahadashas.length} Mahadashas</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-green-400" />
            <span className="text-white/60">{antardashas.length} Antardashas</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-yellow-400" />
            <span className="text-white/60">{pratyantardashas.length} Pratyantardashas</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-pink-400" />
            <span className="text-white/60">{sookshmadashas.length} Sookshma Dashas</span>
          </div>
        </div>
      </div>

      {/* Mahadasha List */}
      <div className="space-y-2">
        {mahadashas.map((md, mdIndex) => {
          const mdPlanet = md.Planet;
          const mdStart = md['Start Date'];
          const mdEnd = md['End Date'];
          const mdCurrent = isCurrent(mdStart, mdEnd);
          const mdDuration = calculateDuration(mdStart, mdEnd);
          const mdExpanded = expandedMD === mdIndex;
          const mdAntardashas = getAntardashas(mdPlanet);

          return (
            <div 
              key={mdIndex} 
              className={`rounded-lg border transition-all ${
                mdCurrent 
                  ? 'bg-gradient-to-r from-yellow-900/30 to-orange-900/30 border-yellow-500/50' 
                  : 'bg-indigo-950/30 border-indigo-800/30'
              }`}
            >
              {/* Mahadasha Header */}
              <div
                className="flex items-center justify-between p-4 cursor-pointer hover:bg-white/5"
                onClick={() => toggleMD(mdIndex)}
              >
                <div className="flex items-center gap-3 flex-1">
                  {mdExpanded ? (
                    <ChevronDown className="w-5 h-5 text-cyan-400" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-cyan-400" />
                  )}
                  <span className={`w-4 h-4 rounded-full ${getPlanetColor(mdPlanet)}`}></span>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-white font-bold text-lg">{mdPlanet} Mahadasha</span>
                      {mdCurrent && (
                        <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full border border-yellow-500/30">
                          CURRENT
                        </span>
                      )}
                    </div>
                    <div className="flex gap-4 text-sm text-white/60 mt-1">
                      <span>{formatDate(mdStart)} → {formatDate(mdEnd)}</span>
                      <span className="text-cyan-400">{mdDuration} years</span>
                      <span className="text-purple-400">{mdAntardashas.length} sub-periods</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Antardasha List */}
              {mdExpanded && (
                <div className="px-4 pb-4 space-y-2">
                  {mdAntardashas.map((ad, adIndex) => {
                    const adPlanet = ad.AD;
                    const adStart = ad.Start;
                    const adEnd = ad.End;
                    const adCurrent = isCurrent(adStart, adEnd);
                    const adDuration = calculateDuration(adStart, adEnd);
                    const adKey = `${mdIndex}-${adIndex}`;
                    const adExpanded = expandedAD[adKey];
                    const adPratyantars = getPratyantardashas(mdPlanet, adPlanet);

                    return (
                      <div
                        key={adIndex}
                        className={`ml-8 rounded-lg border transition-all ${
                          adCurrent
                            ? 'bg-gradient-to-r from-green-900/20 to-teal-900/20 border-green-500/40'
                            : 'bg-indigo-900/20 border-indigo-700/30'
                        }`}
                      >
                        {/* Antardasha Header */}
                        <div
                          className="flex items-center justify-between p-3 cursor-pointer hover:bg-white/5"
                          onClick={() => toggleAD(mdIndex, adIndex)}
                        >
                          <div className="flex items-center gap-2 flex-1">
                            {adExpanded ? (
                              <ChevronDown className="w-4 h-4 text-green-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-green-400" />
                            )}
                            <span className={`w-3 h-3 rounded-full ${getPlanetColor(adPlanet)}`}></span>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-white font-medium">
                                  {mdPlanet}-{adPlanet} Antardasha
                                </span>
                                {adCurrent && (
                                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full border border-green-500/30">
                                    RUNNING
                                  </span>
                                )}
                              </div>
                              <div className="flex gap-3 text-xs text-white/50 mt-0.5">
                                <span>{formatDate(adStart)} → {formatDate(adEnd)}</span>
                                <span className="text-green-400">{adDuration} years</span>
                                <span className="text-yellow-400">{adPratyantars.length} sub-periods</span>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Pratyantardasha List */}
                        {adExpanded && (
                          <div className="px-3 pb-3 space-y-1">
                            {adPratyantars.map((pd, pdIndex) => {
                              const pdPlanet = pd.PD;
                              const pdStart = pd.Start;
                              const pdEnd = pd.End;
                              const pdCurrent = isCurrent(pdStart, pdEnd);
                              const pdDuration = calculateDuration(pdStart, pdEnd);
                              const pdKey = `${mdIndex}-${adIndex}-${pdIndex}`;
                              const pdExpanded = expandedPD[pdKey];
                              const pdSookshmas = getSookshmadashas(mdPlanet, adPlanet, pdPlanet);

                              return (
                                <div
                                  key={pdIndex}
                                  className={`ml-6 rounded border transition-all ${
                                    pdCurrent
                                      ? 'bg-gradient-to-r from-yellow-900/20 to-amber-900/20 border-yellow-500/40'
                                      : 'bg-indigo-800/20 border-indigo-600/30'
                                  }`}
                                >
                                  {/* Pratyantardasha Header */}
                                  <div
                                    className="flex items-center justify-between p-2 cursor-pointer hover:bg-white/5"
                                    onClick={() => togglePD(mdIndex, adIndex, pdIndex)}
                                  >
                                    <div className="flex items-center gap-2 flex-1">
                                      {pdExpanded ? (
                                        <ChevronDown className="w-3 h-3 text-yellow-400" />
                                      ) : (
                                        <ChevronRight className="w-3 h-3 text-yellow-400" />
                                      )}
                                      <span className={`w-2 h-2 rounded-full ${getPlanetColor(pdPlanet)}`}></span>
                                      <div className="flex-1">
                                        <div className="flex items-center gap-2">
                                          <span className="text-white text-sm">
                                            {mdPlanet}-{adPlanet}-{pdPlanet}
                                          </span>
                                          {pdCurrent && (
                                            <span className="px-1.5 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                                              NOW
                                            </span>
                                          )}
                                        </div>
                                        <div className="flex gap-2 text-xs text-white/40 mt-0.5">
                                          <span>{formatDate(pdStart)} → {formatDate(pdEnd)}</span>
                                          <span className="text-yellow-400">{pdDuration}y</span>
                                          <span className="text-pink-400">{pdSookshmas.length} SD</span>
                                        </div>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Sookshma Dasha List */}
                                  {pdExpanded && pdSookshmas.length > 0 && (
                                    <div className="px-2 pb-2 space-y-1 max-h-96 overflow-y-auto">
                                      {pdSookshmas.map((sd, sdIndex) => {
                                        const sdPlanet = sd.SD;
                                        const sdStart = sd.Start;
                                        const sdEnd = sd.End;
                                        const sdCurrent = isCurrent(sdStart, sdEnd);
                                        const sdDuration = calculateDuration(sdStart, sdEnd);

                                        return (
                                          <div
                                            key={sdIndex}
                                            className={`ml-4 p-2 rounded text-xs border transition-all ${
                                              sdCurrent
                                                ? 'bg-pink-900/30 border-pink-500/50'
                                                : 'bg-indigo-700/20 border-indigo-500/30'
                                            }`}
                                          >
                                            <div className="flex items-center gap-2">
                                              <span className={`w-2 h-2 rounded-full ${getPlanetColor(sdPlanet)}`}></span>
                                              <span className="text-white">
                                                {mdPlanet}-{adPlanet}-{pdPlanet}-{sdPlanet}
                                              </span>
                                              {sdCurrent && (
                                                <span className="px-1.5 py-0.5 bg-pink-500/30 text-pink-300 text-xs rounded-full">
                                                  ACTIVE
                                                </span>
                                              )}
                                            </div>
                                            <div className="text-white/40 mt-1">
                                              {formatDate(sdStart)} → {formatDate(sdEnd)}
                                              <span className="ml-2 text-pink-400">{sdDuration}y</span>
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div className="bg-indigo-950/50 rounded-lg p-4 border border-indigo-800/30">
        <h4 className="text-white font-medium mb-3">Planet Legend</h4>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
          {Object.entries(planetColors).map(([planet, color]) => (
            <div key={planet} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${color}`}></span>
              <span className="text-white/70 text-sm">{planet}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default VimshottariDashaComplete;
